import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { FaEye, FaFileInvoiceDollar } from "react-icons/fa"; 
import { CgEnter } from "react-icons/cg";
import Grid from "../components/Grid"; // assuming same reusable Grid
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function PurchaseOrders({ onAddPurchaseOrderClick, onAddGRNClick }) {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("order_code"); // Default search field

  useEffect(() => {
    const fetchUserRole = async () => {
      const token = localStorage.getItem("token");
      if (!token) return toast.error("No auth token found.");

      try {
        const res = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserRole(res.data.role);
      } catch (err) {
        toast.error("Failed to fetch user role.");
      }
    };

    const fetchPurchaseOrders = async () => {
      try {
        const token = localStorage.getItem("token");  
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
    
        const response = await axios.get(`${API_BASE_URL}/purchaseOrders/getPurchaseOrders`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Purchase Orders:", response.data); // Debugging line 

        const formattedPurchaseOrders = response.data.map((order) => ({
          id: order._id,
          order_code: order.purchase_order_code,
          supplier_name: order.supplier.supplier_name || "N/A",
          order_date: new Date(order.order_date).toLocaleDateString("en-US"),
          status: order.status,
          total_amount: order.total_amount,
          items: order.items.map((item) => ({
            product_id: item.product._id,
            product_name: item.product.product_name || "N/A",
            quantity: item.quantity,
            remaining_quantity: item.remaining_quantity,
            unit_price: item.unit_price,
            total_price: item.total_price,
          })),
          created_by: order.created_by ? { username: order.created_by.username, id: order.created_by._id } : null,
        }));
        //const orders = response.data?.purchaseOrders || [];
        setPurchaseOrders(formattedPurchaseOrders);

        console.log("Formatted Purchase Orders:", formattedPurchaseOrders); // Debugging line
    
      } catch (error) {
        console.error("Error fetching purchase orders:", error);
        toast.error("Failed to fetch purchase orders.");
        setPurchaseOrders([]); // Ensure it's always an array
      }
    };
    

    fetchPurchaseOrders();
    fetchUserRole();
  }, []);

  const handleAddGRN = (order) => {
    console.log("Navigating to AddGRN with data:", {
      orderId: order.id, // now `id` is the actual MongoDB _id
      order: order,
    });
  
    onAddGRNClick({
      orderId: order.id,
      order,
    });
  };

  const filteredOrders = purchaseOrders
  .filter((order) => {
    switch (selectedSearchField) {
      case "order_code":
        return order.order_code?.toLowerCase().includes(searchTerm.toLowerCase());
      case "supplier_name":
        return order.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
      case "status":
        return order.status?.toLowerCase().includes(searchTerm.toLowerCase());
      case "total_amount":
        return order.total_amount?.toString().includes(searchTerm);
      case "order_date":
        return order.order_date?.toLowerCase().includes(searchTerm.toLowerCase());
      case "crated_by":
        return order.created_by?.username?.toLowerCase().includes(searchTerm.toLowerCase());
      default:
        return true; 
    }
  });

  const handleDownloadExcel = async () => {
    if (filteredOrders.length === 0) {
      toast.warn("No purchase orders to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Purchase Orders");

    // Define header row
    const headerRow = [
      "Order ID",
      "Supplier Name",
      "Order Date",
      "Total Amount (LKR)",
      "Status",
      "Created By"
    ];
    worksheet.addRow(headerRow);

    // Style header row
    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    filteredOrders.forEach((order) => {
      worksheet.addRow([
        order.order_code,
        order.supplier_name,
        order.order_date,
        Number(order.total_amount).toFixed(2),
        order.status,
        order.created_by?.username || "N/A",
      ]);
    });

    // Adjust column widths based on header length or content length
    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    // Generate buffer and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "purchase_orders.xlsx");
  };

  const columns = [
    { field: "order_code", headerName: "Order ID", flex: 1 },
    { field: "order_date", headerName: "Created Date", flex: 1, headerAlign: "center", align: "center", },
    { field: "supplier_name", headerName: "Supplier", flex: 1 },
    {field: "total_amount", 
      headerName: "Total Amount (LKR)", 
      headerAlign: "right",
      flex: 1,
       align: "right", renderCell: (params) => 
        `${new Intl.NumberFormat(
          'en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 })
          .format(params.value)}` 
        },
    { 
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row.status;
        // Map statuses to classes
        const statusClassMap = {
          "Drafted": "bg-gray-400 text-white",
          "In-Progress": "bg-blue-400 text-white",
          "Completed": "bg-green-400 text-white",
        };
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    { 
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span>
          {params.row.created_by?.username || "N/A"}
        </span>
      ),
    },
    {
     field: "action",
    headerName: "Action",
    flex: 1,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <div className="flex justify-center items-center p-3 space-x-3">
        <button
          onClick={() => setSelectedOrder(params.row)}
          className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
        >
          <FaEye className="text-white" />
        </button>
        <button
          onClick={() => handleAddGRN(params.row)}
          className="bg-[#8BC34A] text-white p-2 flex items-center justify-center"
        >
          <CgEnter className="text-white" />
        </button>
      </div>
    ),
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-green-700">Purchase Orders</h1>
          {userRole === "Company Admin" && (
            <button
              onClick={onAddPurchaseOrderClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Purchase Order
            </button>
          )}
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="order_code">Order ID</option>
                <option value="supplier_name">Supplier Name</option>
                <option value="status">Status</option>
                <option value="total_amount">Total Amount</option>
                <option value="order_date">Order Date</option>
                <option value="crated_by">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          
          <Grid
            rows={filteredOrders} columns={columns} getRowId={(row) => row.id}
          />
        </div>

        {selectedOrder && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={() => setSelectedOrder(null)}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                Purchase Order Details: {selectedOrder.purchase_order_code}
              </h2>
              <div>
                <p><strong>Supplier:</strong> {selectedOrder.supplier_name || "N/A"}</p>
                <p><strong>Total Amount :</strong> LKR {Number(selectedOrder.total_amount).toFixed(2)}</p>
                <p><strong>Status:</strong> {selectedOrder.status}</p>
                <h3 className="text-lg font-semibold mt-4 mb-2">Items</h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedOrder.items.map((item, index) => ({
                      id: index + 1,
                      productName: item.product_name || item.product_id,
                      quantity: item.quantity,
                      unitPrice: parseFloat(item.unit_price).toFixed(2),
                      totalPrice: parseFloat(item.total_price).toFixed(2),
                    }))}
                    columns={[
                      { field: "id", headerName: "ID", width: 70, flex: 1},
                      { field: "productName", headerName: "Product", flex: 1 },
                      { field: "quantity", headerName: "Quantity", width: 120, headerAlign: "right", align: "right", flex: 1 },
                      { field: "unitPrice", headerName: "Unit Price (LKR)", width: 120, headerAlign: "right", align: "right", flex: 1 },
                      { field: "totalPrice", headerName: "Total Price (LKR)", width: 140, headerAlign: "right", align: "right", flex: 1 },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default PurchaseOrders;
